/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/useTrailingEmptyRows.ts
 * FILE_ID: QB-AGGRID-TRAILING-PADS-HOOK-001
 * PURPOSE: Memoised React hook that combines real line items + SAM-style
 *          trailing blank pad rows into the row array AG-Grid renders.
 * WHY: ScopeAgGridSection should stay under 300 lines and the row
 *      projection has its own caching concerns (don't rebuild pads on
 *      every keystroke, but DO rebuild them when configDefaults change).
 * FLOW:
 *   ┌────────────┐   ┌────────────────────────┐   ┌────────────┐
 *   │ realRows   │ → │ buildDisplayRows(seed) │ → │ rows (out) │
 *   └────────────┘   └────────────────────────┘   └────────────┘
 *      seed depends on: sectionId, sortOrder offset,
 *      configDefaults.defaultProductivityPmpd, configDefaults.defaultUnit
 *      → memo invalidates only when those change.
 * DEPENDENCIES:
 *   - pad-row-helpers (QB-AGGRID-PAD-ROWS-001): seed → row factory
 * RELATED:
 *   - ScopeAgGridSection (QB-AGGRID-SECTION-001) — consumer
 */

import { useMemo } from 'react'
import type { QuoteLineItem } from '@/lib/quoteApi'
import {
  buildDisplayRows,
  isPadRowId,
  MIN_TRAILING_PAD_ROWS,
  type PadRowSeed,
} from './pad-row-helpers'

export interface UseTrailingEmptyRowsArgs {
  realRows: QuoteLineItem[]
  sectionId: string
  configDefaults: {
    defaultProductivityPmpd?: number | string | null
    defaultUnit?: string
  }
  /**
   * Override the default trailing count (mostly for tests; production
   * callers should leave it at the default to stay SAM-consistent).
   */
  minPads?: number
  /**
   * When false (read-only quotes / archived states) the hook returns
   * realRows verbatim — no pads, because the user can't add anyway.
   */
  isEditable: boolean
}

export interface UseTrailingEmptyRowsResult {
  /** Rows AG-Grid renders: realRows followed by N pads (or realRows alone if not editable). */
  displayRows: QuoteLineItem[]
  /** Predicate: is this row id one of the trailing pads? */
  isPadRow: (id: string | null | undefined) => boolean
  /** The seed used to build pads — exposed so the parent can build a
   *  promote payload for the create endpoint. */
  padSeed: PadRowSeed
}

function coerceNumber(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const n = Number(value)
    if (Number.isFinite(n)) return n
  }
  return fallback
}

export function useTrailingEmptyRows(
  args: UseTrailingEmptyRowsArgs,
): UseTrailingEmptyRowsResult {
  const {
    realRows,
    sectionId,
    configDefaults,
    isEditable,
    minPads = MIN_TRAILING_PAD_ROWS,
  } = args

  const padSeed: PadRowSeed = useMemo(
    () => ({
      sectionId,
      // Real rows are 1-indexed in the UI but sort_order is the persistence
      // key — using realRows.length+1 onwards keeps pads after every real row.
      sortOrder: realRows.length + 1,
      defaultProductivityPmpd: coerceNumber(
        configDefaults.defaultProductivityPmpd,
        15,
      ),
      defaultUnit: configDefaults.defaultUnit ?? 'm2',
    }),
    [
      sectionId,
      realRows.length,
      configDefaults.defaultProductivityPmpd,
      configDefaults.defaultUnit,
    ],
  )

  const displayRows = useMemo(() => {
    if (!isEditable) return realRows
    return buildDisplayRows(realRows, padSeed, minPads)
  }, [realRows, padSeed, minPads, isEditable])

  return { displayRows, isPadRow: isPadRowId, padSeed }
}
